<script setup lang="ts">
import { mdiDiamondStone, mdiBookshelf, mdiMap, mdiMagicStaff, mdiGiftOutline, mdiStorefrontOutline, mdiOpenInNew } from '@mdi/js'
import type { Product } from '~/composables/useApi'

const api = useApi()
const gameStateStore = useGameStateStore()
const { user: userInfo } = storeToRefs(gameStateStore)

const isChild = computed(() => userInfo.value?.isChild === true)

// Tabs
const tab = ref<string>('encodia')

// Product items.
const items = ref<Array<Product>>([])
const pollingTimer = ref<ReturnType<typeof setTimeout> | null>(null)
const fetchItems = async (): Promise<void> => {
// Clear existing timer, for safety.
  if (pollingTimer.value) {
    clearTimeout(pollingTimer.value)
    pollingTimer.value = null
  }
  // // Fetch products from API.
  // const response = await api.listProducts()
  // items.value = response.products
  // // Set a new timer to fetch products again in 10 seconds.
  // pollingTimer.value = setTimeout(fetchItems, 10000)

  try {
    // Fetch products from API.
    const response = await api.listProducts()
    items.value = response.products

    // Check if there is any item that is processing.
    const isProcessing = items.value.some(item => item.isProcessing)

    if (isProcessing) {
      console.log('There is an item that is processing. Fetching again in 5 seconds...')

      // Fetch again in 5 seconds.
      pollingTimer.value = setTimeout(() => {
        fetchItems()
      }, 5000)
    }
    else {
      console.log('There is no item that is processing. Stopping polling.')
    }
  }
  catch (error) {
    console.error('Failed to fetch product list', error)
  }
}

onMounted(async () => {
  await fetchItems()
})

// Stop polling when the page is unloaded.
onUnmounted(() => {
  if (pollingTimer.value) {
    clearTimeout(pollingTimer.value)
  }
})

// Payment dialog.
const paymentDialog = ref<boolean>(false)
const selectedPriceId = ref<string | null>(null)
const selectedProduct = ref<Product | null>(null)
const paymentMode = ref<'payment' | 'subscription'>('payment')
const defaultHeaderText = computed(() =>
  isChild.value
    ? '商品の購入は保護者アカウントからお願いするんだぞ。ギフトカードを持っているなら、ここでキーを教えてくれ。'
    : 'さぁ、詳しく知りたいものを選んでくれ。',
)
const headerText = ref<string>(defaultHeaderText.value)

watch(defaultHeaderText, (value) => {
  headerText.value = value
})

const setSelectedProduct = (priceId: string): void => {
  selectedPriceId.value = priceId
  selectedProduct.value = items.value.find(item => item.priceId === priceId) || null
  headerText.value = selectedProduct.value?.description || ''
  paymentMode.value = selectedProduct.value?.billingCycle === 'monthly' || selectedProduct.value?.billingCycle === 'annually' ? 'subscription' : 'payment'
  console.log('paymentMode', paymentMode.value)
}

const onClickProduct = (priceId: string): void => {
  setSelectedProduct(priceId)
}

const onSubmitProduct = (priceId: string): void => {
  if (isChild.value) return
  setSelectedProduct(priceId)
  paymentDialog.value = true
}

// #719 観点B: モバイルは横型リストカードで表示（購入ロジックは共通のまま）。
const { isMobile } = useIsMobile()

const plainText = (s: string): string =>
  (s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

const priceLabel = (p: Product): string => {
  const yen = `¥${(p.amount ?? 0).toLocaleString()}`
  if (p.billingCycle === 'monthly') return `${yen}/月`
  if (p.billingCycle === 'annually') return `${yen}/年`
  return yen
}

// #721: モバイルは「タップ＝説明ダイアログ／その中の購入ボタン＝Stripe」に分離する
// （従来はタップが説明反映＋購入ダイアログ一体で「見てから買う」がしづらかった）。
const detailDialog = ref<boolean>(false)
const detailProduct = ref<Product | null>(null)

// 消耗品（isRepeatable）は購入済でも再購入できる。エリア解放など一回きりの商品は
// 購入済なら購入導線を出さない。処理中はどちらも不可。
const canPurchase = (item: Product | null): boolean =>
  !!item && !isChild.value && !item.isProcessing && (item.isRepeatable || !item.isPurchased)

// 購入済の消耗品は「もう一度購入」で再購入の意図を明示する。
const purchaseButtonLabel = (item: Product | null): string =>
  item?.isPurchased && item.isRepeatable ? 'もう一度購入' : '購入する'

const onMobileProductClick = (item: Product): void => {
  detailProduct.value = item
  detailDialog.value = true
}

const onDetailPurchase = (): void => {
  const item = detailProduct.value
  if (!canPurchase(item)) return
  onSubmitProduct(item!.priceId) // Stripe 購入ダイアログを開く
}

const dialogStore = useDialogStore()

const onClickGiftKey = (): void => {
  headerText.value = 'ギフトカードを持っているかい？ここでキーを教えてもらえれば、特典を付与してやろう。'
}

const onSubmitGiftKey = (): void => {
  // #721: 実際の redeem は SceneMap 側の KeyInputDialog（keyMode='gift'）が担う。
  // ここはダイアログを開くだけ（desktop/mobile 共通）。
  dialogStore.showDialog('key', { keyMode: 'gift' })
}
</script>

<template>
  <div class="shelf-root">
    <!-- #719 観点B: モバイル（横型リストカード） -->
    <div
      v-if="isMobile"
      class="shelf-mobile"
    >
      <div class="shelf-mobile__header">
        <v-img
          src="@/assets/images/story/Icon_broker.webp"
          cover
          class="shelf-mobile__icon"
        />
        <div class="shelf-mobile__header-text">
          <MDC :value="headerText" />
        </div>
      </div>

      <div class="shelf-mobile__list">
        <!-- #721 2巡目: 商品画像は「ふちなし（全面）」にする。image-fit の既定 cover を
             使う（contain だと MobileListCard 側で 6px の余白が付き、魔法研究会の全面
             表示と見た目が揃わなかった）。 -->
        <MobileListCard
          v-for="item in items"
          :key="item.priceId"
          :image="productIconFor(item.productId)"
          :title="item.title"
          :subtitle="plainText(item.description)"
          @click="onMobileProductClick(item)"
        >
          <template #meta>
            <span class="shelf-mobile__price">{{ priceLabel(item) }}</span>
            <span
              v-if="item.isPurchased"
              class="shelf-mobile__owned"
            >購入済み</span>
            <span
              v-else-if="item.isProcessing"
              class="shelf-mobile__processing"
            >処理中…</span>
          </template>
        </MobileListCard>
      </div>

      <!-- ギフトキー: 埋もれないよう目立つカードにする -->
      <button
        v-if="!isChild"
        type="button"
        class="shelf-mobile__gift"
        @click="onSubmitGiftKey"
      >
        <v-icon
          :icon="mdiGiftOutline"
          size="26"
        />
        <span class="shelf-mobile__gift-body">
          <span class="shelf-mobile__gift-title">ギフトキーを使う</span>
          <span class="shelf-mobile__gift-sub">お持ちのカードのキーで特典を受け取る</span>
        </span>
      </button>

      <!-- BOOTH: 匿名配送の別導線。テキストリンクだと見落とされるのでカード化 -->
      <a
        href="https://codeandmagic.booth.pm/"
        target="_blank"
        rel="noopener noreferrer"
        class="shelf-mobile__booth"
      >
        <v-icon
          :icon="mdiStorefrontOutline"
          size="26"
        />
        <span class="shelf-mobile__booth-body">
          <span class="shelf-mobile__booth-title">BOOTH店（匿名配送）</span>
          <span class="shelf-mobile__booth-sub">匿名配送に対応した外部ストアはこちら</span>
        </span>
        <v-icon
          :icon="mdiOpenInNew"
          size="18"
          class="shelf-mobile__booth-ext"
        />
      </a>
    </div>

    <!-- デスクトップ（従来の棚 UI）。flex 子の関係を保つため template でグループ化 -->
    <template v-else>
      <div class="content-header">
        <v-row>
          <v-col cols="auto">
            <v-img
              src="@/assets/images/story/Icon_broker.webp"
              cover
              class="header-icon"
            />
          </v-col>
          <v-col>
            <MDC :value="headerText" />
          </v-col>
        </v-row>
      </div>

      <v-tabs
        v-if="false"
        v-model="tab"
        class="content-tab"
      >
        <v-tab value="encodia">
          <v-icon>{{ mdiMap }}</v-icon>エンコーディア探索
        </v-tab>
        <v-tab
          value="arcanelab"
          disabled
        >
          <v-icon>{{ mdiDiamondStone }}</v-icon>魔法研究会
        </v-tab>
        <v-tab
          value="cradle"
          disabled
        >
          <v-icon>{{ mdiBookshelf }}</v-icon>羽根筆のゆりかご
        </v-tab>
        <v-tab
          value="support"
          disabled
        >
          <v-icon>{{ mdiMagicStaff }}</v-icon>大賢者のサポート
        </v-tab>
      </v-tabs>

      <v-card-text class="content-area">
        <v-tabs-window v-model="tab">
          <v-tabs-window-item value="encodia">
            <EmporiumProductList
              :items="items"
              show-gift-key
              :purchase-disabled="isChild"
              @submit="onSubmitProduct"
              @click="onClickProduct"
              @click-gift-key="onClickGiftKey"
              @submit-gift-key="onSubmitGiftKey"
            />
          </v-tabs-window-item>

          <v-tabs-window-item value="arcanelab">
            <EmporiumProductList
              :items="items"
              :categories="['plan']"
              :purchase-disabled="isChild"
            />
          </v-tabs-window-item>

          <v-tabs-window-item
            v-if="false"
            value="cradle"
          >
            <EmporiumProductList
              :items="items"
              :categories="['support']"
              :purchase-disabled="isChild"
            />
          </v-tabs-window-item>

          <v-tabs-window-item value="support">
            <EmporiumProductList
              :items="items"
              :categories="['support']"
              :purchase-disabled="isChild"
            />
          </v-tabs-window-item>
        </v-tabs-window>
        <div class="booth-link-area">
          <span class="booth-link-text">匿名配送に対応した</span>
          <a
            href="https://codeandmagic.booth.pm/"
            target="_blank"
            rel="noopener noreferrer"
            class="booth-link"
          >BOOTH店</a>
          <span class="booth-link-text">もご利用いただけます。</span>
        </div>
      </v-card-text>
    </template>

    <!-- #721 モバイル: 商品詳細ダイアログ（説明を読んでから「購入する」で Stripe へ）。 -->
    <Dialog
      v-if="detailProduct"
      v-model="detailDialog"
      :title="detailProduct.title"
      :cancel-button="{ title: 'とじる' }"
      :submit-button="canPurchase(detailProduct)
        ? { title: purchaseButtonLabel(detailProduct), effect: 'glow', color: 'primary' }
        : undefined"
      @submit="onDetailPurchase"
    >
      <template #default>
        <div class="product-detail">
          <img
            class="product-detail__img"
            :src="productIconFor(detailProduct.productId)"
            :alt="detailProduct.title"
          >
          <p class="product-detail__price">
            {{ priceLabel(detailProduct) }}
            <span
              v-if="detailProduct.isPurchased"
              class="product-detail__owned"
            >購入済み</span>
          </p>
          <div class="product-detail__desc">
            <MDC :value="detailProduct.description || ''" />
          </div>
          <p
            v-if="isChild"
            class="product-detail__note"
          >
            商品の購入は保護者アカウントからお願いします。
          </p>
        </div>
      </template>
    </Dialog>

    <EmporiumPurchaseDialog
      v-if="paymentDialog && selectedPriceId"
      v-model="paymentDialog"
      :price-id="selectedPriceId"
      :title="selectedProduct?.title || ''"
      :mode="paymentMode"
    />
    <!-- #721: KeyInputDialog は置かない。ProductShelf は desktop/mobile とも
         SceneMap 内にネストされるので、ギフトキーは SceneMap 側の KeyInputDialog
         （keyMode='gift' で redeem + game state reload + 計測）が一括処理する。
         ここに置くと二重レンダー（重なり・二重処理）になる。 -->
  </div>
</template>

<style scoped>
.shelf-root {
    display: flex;
    flex-direction: column;
    height: 100%;
    /* overflow: hidden; */
}

.header-icon {
    width: max(48px, calc(9 * var(--u)));
    height: max(48px, calc(9 * var(--u)));
}

.content-header {
    /* background-color: #f5d38c; */
    /* background-color: rgb(196, 96, 96, 0.7); */
    background-color: rgb(192, 192, 192, 0.8);
    /* background-image: url("@/assets/images/556492_800_m.webp"); */
    /* background-size: 100% 100%; */

    font-family: "Utsukushi" !important;
    /* font-size: 2rem !important; */
    font-size: max(13.33px, calc(1.875 * var(--u))) !important;
    font-weight: bold;
    color: black;
    text-shadow:
        1px 0 white,
        -1px 0 white,
        0 1px white,
        0 -1px white,
        1px 1px white,
        -1px -1px white,
        1px -1px white,
        -1px 1px white;

    /* width: 800px; */
    /* width: clamp(444.44px, 62.5dvw, 800px); */
    width: 98%;
    /* justify-content: center; */
    /* align-items: center; */
    align-self: center;
    /* height: 635px !important; */
    /* height: clamp(352.78px, 40.61dvw, 635px) !important; */
    /* height: clamp(355.56px, 50dvw, 640px); */

    /* padding: 24px 32px; */
    padding: max(6.67px, calc(0.94 * var(--u)));
    /* padding-left: clamp(17.78px, 2.5dvw, 32px); */
    /* padding-right: clamp(17.78px, 2.5dvw, 32px); */
    user-select: none;
}

.content-tab {
    margin-left: 1%;

    height: max(24px, calc(3.438 * var(--u)));
}

.content-tab .v-tab {
    font-family: "Noto Sans JP", sans-serif;
    font-weight: bold;
    font-size: max(8.89px, calc(1.25 * var(--u)));

    /* height: clamp(22.22px, 3.125dvw, 36px); */
    height: max(24px, calc(3.438 * var(--u)));
    background-color: rgb(196, 96, 96, 0.7);

    text-shadow:
        1px 0 white,
        -1px 0 white,
        0 1px white,
        0 -1px white,
        1px 1px white,
        -1px -1px white,
        1px -1px white,
        -1px 1px white;
}

.content-area {
    /* height: calc(100% - 500px); */
    /* height: 60vh; */
    /* height: 74%; */
    /* max-height: 34%; */
    /* height: 100%; */
    /* padding: 0 1%; */
    /* padding: 0; */
    /* padding-bottom: clamp(4.45px, 0.75dvw, 8px); */
    /* padding: clamp(4.45px, 0.75dvw, 8px); */
    flex: 1;
    /* overflow: hidden; */
    overflow-y: auto;
    scrollbar-color: darkred black;
    scrollbar-width: thin;

    /* padding: clamp(2.22px, 0.38dvw, 4px); */
    /* padding-bottom: clamp(4.45px, 0.75dvw, 8px); */
    padding: max(2px, calc(0.5 * var(--u)));

    background-color: rgb(128, 64, 64, 0.5);
}

.booth-link-area {
    text-align: center;
    font-family: "Noto Sans JP", sans-serif;
    font-size: max(10px, calc(1.25 * var(--u)));
}

.booth-link-text {
    color: rgba(255, 255, 255, 0.7);
    font-weight: bold;
}

.booth-link {
    color: #90caf9;
    text-decoration: none;
}

.booth-link:hover {
    text-decoration: underline;
}

/* #719 観点B: モバイルの交易所（mobile-nav レイアウト内・dvw/dvh 可）。 */
.shelf-mobile {
  padding: 4px 10px 16px;
  font-family: "Noto Sans JP", sans-serif;
  max-width: 560px;
  margin: 0 auto;
}

.shelf-mobile__header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  margin-bottom: 10px;
  background: rgba(224, 224, 224, 0.9);
  border-radius: 10px;
  color: #000;
}

.shelf-mobile__icon {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 50%;
}

.shelf-mobile__header-text {
  font-size: 0.85rem;
  line-height: 1.4;
}

.shelf-mobile__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.shelf-mobile__price {
  font-weight: 700;
  color: #b5341a;
  font-size: 0.95rem;
}

.shelf-mobile__owned {
  font-size: 0.72rem;
  color: #fff;
  background: #4bbf81;
  border-radius: 6px;
  padding: 1px 7px;
}

.shelf-mobile__processing {
  font-size: 0.72rem;
  color: #fff;
  background: #90a4ae;
  border-radius: 6px;
  padding: 1px 7px;
}

/* ギフトキー: 埋もれないよう塗りのカードで目立たせる */
.shelf-mobile__gift {
  margin-top: 18px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #c9812f, #a35d1a);
  color: #fff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.28);
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.shelf-mobile__gift:active {
  transform: scale(0.99);
}

.shelf-mobile__gift-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.shelf-mobile__gift-title {
  font-weight: 700;
  font-size: 1rem;
}

.shelf-mobile__gift-sub {
  font-size: 0.74rem;
  color: rgba(255, 255, 255, 0.85);
}

/* BOOTH: 匿名配送の別導線。テキストリンクだと見落とすのでカード化 */
.shelf-mobile__booth {
  margin-top: 12px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
}

.shelf-mobile__booth-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.shelf-mobile__booth-title {
  font-weight: 700;
  font-size: 0.95rem;
}

.shelf-mobile__booth-sub {
  font-size: 0.74rem;
  color: rgba(255, 255, 255, 0.8);
}

.shelf-mobile__booth-ext {
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.85);
}

/* 商品詳細ダイアログ（全画面 Dialog 内の本文） */
.product-detail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.product-detail__img {
  width: 40%;
  max-width: 180px;
  aspect-ratio: 1;
  object-fit: contain;
}

.product-detail__price {
  margin: 0;
  /* Utsukushi は ¥ グリフを持たないため、価格だけ Noto Sans JP にする。 */
  font-family: "Noto Sans JP", sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #b5341a;
}

.product-detail__owned {
  margin-left: 8px;
  font-size: 0.8rem;
  color: #fff;
  background: #4bbf81;
  border-radius: 6px;
  padding: 2px 8px;
  vertical-align: middle;
}

.product-detail__desc {
  align-self: stretch;
  font-size: 1rem;
  line-height: 1.7;
}

.product-detail__note {
  align-self: stretch;
  margin: 0;
  font-size: 0.85rem;
  color: #7a3b12;
}
</style>
